import type { VisualRendererFactory } from '../../visual/index.js';
import type { RenderingPrototype } from '../prototype/RenderingPrototype.js';
import { MandalaRenderer } from './MandalaRenderer.js';
import { MathRenderer } from './MathRenderer.js';
import { RippleArtRenderer } from './RippleArtRenderer.js';
import { InkPaintRenderer } from './InkPaintRenderer.js';
import { SmokeRenderer } from './SmokeRenderer.js';
import { SumiRenderer } from './SumiRenderer.js';
import { WatercolorRenderer } from './WatercolorRenderer.js';

/**
 * Where the art engine meets three, and the only place it does.
 *
 * The engine holds this map and calls into it; it never imports a renderer, a
 * scene or the library they are written against. Adding `SMOKE` later means
 * writing the renderer and adding one line here — no engine change, no recipe
 * schema change, and a recipe that names a renderer this build does not have
 * degrades to "that layer is skipped" rather than to a crash.
 */

export interface RendererFactoryOptions {
  readonly prototype: RenderingPrototype;
  /**
   * Live values the signal bus does not carry — the song's name, mostly.
   *
   * A callback rather than a snapshot because renderers outlive any one song.
   */
  readonly describe?: () => Readonly<Record<string, string | number>>;
  /** Lowers the per-renderer budget. See the performance note in the report. */
  readonly rippleCapacity?: number;
  readonly smokeCapacity?: number;
  readonly inkCapacity?: number;
}

export function createRendererFactories(
  options: RendererFactoryOptions,
): Readonly<Record<string, VisualRendererFactory>> {
  const { prototype } = options;
  const scene = prototype.stage.scene;
  const fieldRadius = prototype.stage.field.radius;

  return {
    MANDALA: (layerId) => new MandalaRenderer({ prototype, layerId }),

    RIPPLE: (layerId) =>
      new RippleArtRenderer({
        scene,
        layerId,
        fieldRadius,
        ...(options.rippleCapacity === undefined ? {} : { capacity: options.rippleCapacity }),
      }),

    MATH: (layerId) =>
      new MathRenderer({
        scene,
        layerId,
        fieldRadius,
        dialect: 'MATH',
        ...(options.describe === undefined ? {} : { describe: options.describe }),
      }),

    SUMI: (layerId) => new SumiRenderer({ scene, layerId, fieldRadius }),

    WATERCOLOR: (layerId) => new WatercolorRenderer({ scene, layerId, fieldRadius }),

    // The only renderer handed the WebGLRenderer itself. It keeps a persistent
    // target that it writes into outside the stage's own render, which is the
    // whole mechanism behind paint that accumulates.
    INK_PAINT: (layerId) =>
      new InkPaintRenderer({
        scene,
        renderer: prototype.stage.renderer,
        layerId,
        fieldRadius,
        ...(options.inkCapacity === undefined ? {} : { capacity: options.inkCapacity }),
      }),

    SMOKE: (layerId) =>
      new SmokeRenderer({
        scene,
        layerId,
        fieldRadius,
        ...(options.smokeCapacity === undefined ? {} : { capacity: options.smokeCapacity }),
      }),

    // Same implementation, different vocabulary. See `MathRenderer`.
    CODE: (layerId) =>
      new MathRenderer({
        scene,
        layerId,
        fieldRadius,
        dialect: 'CODE',
        ...(options.describe === undefined ? {} : { describe: options.describe }),
      }),
  };
}
